import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, ArrowRight, Save, Info, Plane, Car, Hotel, Compass, Ticket, Plus, Shield, DollarSign, CheckCircle2, Circle, Copy, ExternalLink, Loader2, AlertTriangle, Ban, MessageSquare } from "lucide-react";
import { ProposalBuilderHeader } from "@/components/proposals/ProposalBuilderHeader";
import {
  getProposal,
  getProposalStatus,
  createProposal,
  updateProposal,
  updateProposalStatus,
  requestProposalCancellation,
  getOrCreateProposalPublicLink,
  type ProposalPricingView,
} from "@/lib/proposalsApi";
import { getClient } from "@/lib/clientsApi";
import { createAdditionalRequestService, updateAdditionalRequestService } from "@/lib/additionalRequestServicesApi";
import { getUser } from "@/lib/usersApi";
import { lookupUsersByRoles } from "@/lib/usersApi";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { hasAnyData, validateAndSanitizeItems } from "@/lib/proposalValidators";
import GeneralInfoStep from "@/components/proposals/GeneralInfoStep";
import AirportServicesStep from "@/components/proposals/AirportServicesStep";
import TransfersStep from "@/components/proposals/TransfersStep";
import FlightsStep from "@/components/proposals/FlightsStep";
import AccommodationStep from "@/components/proposals/AccommodationStep";
import ToursStep from "@/components/proposals/ToursStep";
import AttractionsStep from "@/components/proposals/AttractionsStep";
import AdditionalRequestsStep from "@/components/proposals/AdditionalRequestsStep";
import InsuranceStep from "@/components/proposals/InsuranceStep";
import HandlingFeeStep from "@/components/proposals/HandlingFeeStep";
import FollowUpStep from "@/components/proposals/FollowUpStep";
import SummaryStep from "@/components/proposals/SummaryStep";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getStatusIndex, isPaidOrBeyond, mapLegacyStatusToNewStatus, statusLocksEditing } from "@/lib/proposalStatus";

const steps = [
  { id: 1, name: "General Info", component: GeneralInfoStep, icon: Info },
  { id: 2, name: "Airport Services", component: AirportServicesStep, icon: Plane },
  { id: 3, name: "Transfers", component: TransfersStep, icon: Car },
  { id: 4, name: "Flights", component: FlightsStep, icon: Plane },
  { id: 5, name: "Accommodation", component: AccommodationStep, icon: Hotel },
  { id: 6, name: "Tours", component: ToursStep, icon: Compass },
  { id: 7, name: "Attractions", component: AttractionsStep, icon: Ticket },
  { id: 8, name: "Additional Requests", component: AdditionalRequestsStep, icon: Plus },
  { id: 9, name: "Insurance", component: InsuranceStep, icon: Shield },
  { id: 10, name: "Handling Fee", component: HandlingFeeStep, icon: DollarSign },
  { id: 11, name: "Follow Up", component: FollowUpStep, icon: MessageSquare },
  { id: 12, name: "Summary", component: SummaryStep, icon: CheckCircle2 },
];

/** Result of a draft save: `ok` plus, on failure, whatever the API layer threw. */
type SaveOutcome = { ok: boolean; error?: { message?: string } };

type ManagerOption = {
  id: string;
  full_name: string;
  email: string;
};

const ProposalBuilder = () => {
  const { id } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [proposalData, setProposalData] = useState<any>({
    pax_adults: 1,
    pax_children: 0,
    pax_infants: 0,
    currency: "usd",
    passengerCount: {
      adults: 1,
      children: 0,
      infants: 0,
    },
    passenger_mode_detailed: false,
    detailedPassengerMode: false,
    pricing_adjustment_type: "none",
    pricing_adjustment_percentage: 0,
  });
  const [loading, setLoading] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([1]));
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareableLink, setShareableLink] = useState("");
  const [generationPricingView, setGenerationPricingView] = useState<ProposalPricingView>("adjusted");
  const [previewingProposal, setPreviewingProposal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false);
  const [cancelReasonInput, setCancelReasonInput] = useState("");
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [requestingCancellation, setRequestingCancellation] = useState(false);
  const statusPollInFlight = useRef(false);
  // Mirror of proposalData so handleSave can read the latest value even when
  // called immediately after setProposalData (closure would otherwise be stale).
  const proposalDataRef = useRef<any>(null);
  useLayoutEffect(() => {
    proposalDataRef.current = proposalData;
  }, [proposalData]);
  // Snapshot of the proposal as last fetched from / persisted to the server.
  // Used to detect whether the local state is dirty so we can skip a no-op
  // PUT before opening preview.
  const lastSavedSnapshotRef = useRef<string | null>(null);
  // Tracks whether the proposal was loaded from the server with any service items. Used as a
  // client-side safety net: a save that would clear every item of a proposal that had them is a
  // bug (stale/empty state), never a real edit, so handleSave refuses to send it.
  const loadedHadItemsRef = useRef<boolean>(false);
  // False until loadProposal has populated state from the server at least once. An existing
  // proposal must never be saved before this is true: before the fetch resolves, proposalData is
  // the empty initial state, and any save (e.g. clicking Preview during the load) would PUT every
  // item list empty and wipe the whole proposal.
  const hasLoadedRef = useRef<boolean>(false);
  const snapshotProposal = (data: any) => {
    try {
      return JSON.stringify(data);
    } catch {
      return null;
    }
  };
  const [ownerProfile, setOwnerProfile] = useState<{ id: string; full_name?: string; profile_photo?: string | null } | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, userRole } = useAuth();
  const isFinanceStaff = userRole === "finance_staff";
  const isAdminRole = userRole === "ceo" || userRole === "general_manager";

  const hasText = (value: any) => typeof value === "string" && value.trim().length > 0;
  const hasPositiveNumber = (value: any) => typeof value === "number" && Number.isFinite(value) && value > 0;
  const hasAny = (items: any[] | undefined, predicate: (item: any) => boolean) =>
    Array.isArray(items) && items.some((item) => predicate(item || {}));
  const normalizeProposalCurrency = (value: any): "usd" | "aed" => {
    const normalized = (value || "").toString().trim().toLowerCase();
    return normalized === "aed" ? "aed" : "usd";
  };

  const stepCompletion = (() => {
    const passengerCount = proposalData?.passengerCount || {};
    const hasPassengers =
      (Number(passengerCount.adults) || 0) > 0 ||
      (Number(passengerCount.children) || 0) > 0 ||
      (Number(passengerCount.infants) || 0) > 0 ||
      (Number(proposalData?.pax_adults) || 0) > 0 ||
      (Number(proposalData?.pax_children) || 0) > 0 ||
      (Number(proposalData?.pax_infants) || 0) > 0;

    const hasTripLegs = hasAny(proposalData?.tripLegs, (leg) =>
      hasText(leg.from_location) || hasText(leg.to_location) || hasText(leg.start_date) || hasText(leg.end_date)
    );

    const generalInfoComplete =
      hasText(proposalData?.title) ||
      hasText(proposalData?.main_destination) ||
      hasText(proposalData?.context_observations) ||
      hasPassengers ||
      hasTripLegs;

    const airportServicesComplete = hasAny(proposalData?.airportServices, (service) =>
      (Number(service.airport_service_id) || 0) > 0 ||
      hasText(service.service_date) ||
      hasText(service.notes)
    );

    const transfersComplete = hasAny(proposalData?.transfers, (transfer) =>
      (Number(transfer.transfer_id) || 0) > 0 ||
      hasText(transfer.transfer_date) ||
      hasText(transfer.notes)
    );

    const flightsComplete = hasAny(proposalData?.flights, (flight) =>
      hasText(flight.airline) ||
      hasText(flight.flight_number) ||
      hasText(flight.flight_class) ||
      hasText(flight.departure_airport) ||
      hasText(flight.arrival_airport) ||
      hasText(flight.departure_datetime) ||
      hasText(flight.arrival_datetime)
    );

    const accommodationsComplete = hasAny(proposalData?.accommodations, (acc) =>
      (Number(acc.hotel_id) || 0) > 0 ||
      (Number(acc.room_id) || 0) > 0 ||
      hasText(acc.check_in) ||
      hasText(acc.check_out) ||
      hasText(acc.cancellation_policy) ||
      hasPositiveNumber(acc.hcte_night_rate) ||
      hasPositiveNumber(acc.accommodation_website_night_rate) ||
      hasPositiveNumber(acc.booking_com_night_rate) ||
      hasPositiveNumber(acc.booking_price) ||
      acc.booking_with_us === true ||
      acc.free_of_charge === true
    );

    const toursComplete = hasAny(proposalData?.tours, (tour) =>
      (Number(tour.tour_id) || 0) > 0 ||
      hasText(tour.tour_date) ||
      hasText(tour.time_from) ||
      hasText(tour.time_to)
    );

    const attractionsComplete = hasAny(proposalData?.attractions, (attraction) =>
      (Number(attraction.attraction_id) || 0) > 0 ||
      (Number(attraction.ticket_id) || 0) > 0 ||
      hasText(attraction.attraction_date) ||
      hasText(attraction.time_from) ||
      hasText(attraction.time_to) ||
      hasText(attraction.notes)
    );

    const additionalRequestsComplete = hasAny(proposalData?.additionalRequests, (req) =>
      hasText(req.request_title) ||
      hasText(req.request_description) ||
      hasText(req.request_date) ||
      hasText(req.request_time) ||
      hasPositiveNumber(req.request_price)
    );

    const insuranceComplete = hasAny(proposalData?.insurance, (ins) =>
      hasText(ins.insurance_title) ||
      hasText(ins.insurance_description) ||
      hasPositiveNumber(ins.insurance_price)
    );

    const handlingFeeComplete = hasAny(proposalData?.handlingFee, (fee) =>
      hasText(fee.fee_title) ||
      hasText(fee.fee_description) ||
      hasPositiveNumber(fee.fee_price)
    );

    const summaryComplete =
      generalInfoComplete ||
      airportServicesComplete ||
      transfersComplete ||
      flightsComplete ||
      accommodationsComplete ||
      toursComplete ||
      attractionsComplete ||
      additionalRequestsComplete ||
      insuranceComplete ||
      handlingFeeComplete;

    return {
      1: generalInfoComplete,
      2: airportServicesComplete,
      3: transfersComplete,
      4: flightsComplete,
      5: accommodationsComplete,
      6: toursComplete,
      7: attractionsComplete,
      8: additionalRequestsComplete,
      9: insuranceComplete,
      10: handlingFeeComplete,
      11: summaryComplete,
    } as Record<number, boolean>;
  })();

  const detailedPassengersCount = Array.isArray(proposalData?.passengers)
    ? proposalData.passengers.length
    : 0;
  const simplePassengersCount =
    (proposalData?.passengerCount?.adults ?? 0) +
    (proposalData?.passengerCount?.children ?? 0) +
    (proposalData?.passengerCount?.infants ?? 0);
  const totalPassengers = Math.max(detailedPassengersCount, simplePassengersCount);
  const normalizedProposalStatus = mapLegacyStatusToNewStatus(proposalData?.status);
  const currentStatusIndex = getStatusIndex(normalizedProposalStatus);
  const readyToBookIndex = getStatusIndex("ready_to_book");
  const isSalesOwner = userRole === "sales_staff" && !!user?.id && !!proposalData?.owner_id && user.id === proposalData.owner_id;
  const isManagerOrCeo = userRole === "general_manager" || userRole === "ceo";
  const canRequestCancellation =
    (isSalesOwner || isManagerOrCeo) &&
    currentStatusIndex !== -1 &&
    currentStatusIndex < readyToBookIndex &&
    normalizedProposalStatus !== "in_review_to_be_canceled" &&
    normalizedProposalStatus !== "canceled";
  const isSummaryStep = currentStep === steps.length;
  const isFollowUpStep = steps[currentStep - 1]?.name === "Follow Up";
  const isInProgress = normalizedProposalStatus === "in_progress";
  const isNeedsFixSales = normalizedProposalStatus === "needs_fix_sales";
  const isCancellationReview = normalizedProposalStatus === "in_review_to_be_canceled";
  const isCanceled = normalizedProposalStatus === "canceled";
  const isCeo = userRole === "ceo";
  // The CEO can reopen a status-locked proposal without moving its status. While it is unlocked
  // the exemption is the CEO's alone -- Sales and General Manager still get the read-only builder.
  const isCeoEditUnlocked = isCeo && !!proposalData?.edit_unlocked;
  const statusFreezesEditing =
    isCancellationReview ||
    isCanceled ||
    normalizedProposalStatus === "approved_to_be_paid" ||
    isPaidOrBeyond(proposalData?.status) ||
    statusLocksEditing(proposalData?.status);
  const isReadOnly = isFinanceStaff || (statusFreezesEditing && !isCeoEditUnlocked);

  const handleProposalDataChange = (nextData: any) => {
    setProposalData(nextData);
  };

  // Persists the proposal immediately after committing an adjustment change.
  // `dataOverride` bypasses the stale closure that handleSave would otherwise
  // capture (state set just above hasn't flushed yet).
  // Summary applies the internal adjustment, price tier and VAT flag optimistically so the
  // recalculated totals show up immediately. When the save behind it fails, the optimistic
  // value has to go back — otherwise the screen keeps showing a number the server rejected,
  // and it silently disappears on the next reload.
  const handleApplyAdjustment = async (nextData: any) => {
    const previousData = proposalDataRef.current;
    setProposalData(nextData);
    if (isReadOnly || !id) return;
    const outcome = await handleSave({ skipReload: true, silent: true, dataOverride: nextData });
    if (!outcome.ok) {
      setProposalData(previousData);
      toast({
        title: "Could not apply the change",
        description: outcome.error?.message || "The proposal was not updated. Please try again.",
        variant: "destructive",
      });
      throw outcome.error ?? new Error("Failed to apply proposal adjustment");
    }
  };

  const normalizeRequestImages = (images: any): string[] => {
    if (!Array.isArray(images)) return [];
    return images
      .filter((image) => typeof image === "string")
      .map((image) => image.trim())
      .filter((image) => image.length > 0)
      .slice(0, 3);
  };

  const syncReusableAdditionalRequests = async (
    items: any[] | undefined,
    options?: { persistCatalog?: boolean }
  ): Promise<any[]> => {
    if (!Array.isArray(items) || items.length === 0) return [];
    const persistCatalog = options?.persistCatalog ?? true;

    const normalizedItems = items.map((item) => ({
      ...item,
      request_images: normalizeRequestImages(item?.request_images),
    }));
    if (!persistCatalog) {
      return normalizedItems;
    }

    return Promise.all(normalizedItems.map(async (normalizedItem) => {
      const shouldSaveToCatalog =
        normalizedItem?.save_to_catalog === true &&
        typeof normalizedItem?.request_title === "string" &&
        normalizedItem.request_title.trim().length > 0;

      if (!shouldSaveToCatalog) {
        return normalizedItem;
      }

      const numericPrice = Number(normalizedItem.request_price ?? 0);
      const payload = {
        request_title: normalizedItem.request_title.trim(),
        request_description:
          typeof normalizedItem.request_description === "string" && normalizedItem.request_description.trim().length > 0
            ? normalizedItem.request_description.trim()
            : null,
        request_price: Number.isFinite(numericPrice) && numericPrice >= 0 ? numericPrice : 0,
        request_images: normalizedItem.request_images,
      };

      if (normalizedItem.additional_request_service_id) {
        const updated = await updateAdditionalRequestService(Number(normalizedItem.additional_request_service_id), payload);
        return {
          ...normalizedItem,
          additional_request_service_id: updated.id,
          request_title: updated.request_title ?? normalizedItem.request_title,
          request_description: updated.request_description ?? normalizedItem.request_description,
          request_price: updated.request_price ?? normalizedItem.request_price,
          request_images: normalizeRequestImages(updated.request_images),
        };
      }
      const created = await createAdditionalRequestService(payload);
      return {
        ...normalizedItem,
        additional_request_service_id: created.id,
        request_title: created.request_title ?? normalizedItem.request_title,
        request_description: created.request_description ?? normalizedItem.request_description,
        request_price: created.request_price ?? normalizedItem.request_price,
        request_images: normalizeRequestImages(created.request_images),
      };
    }));
  };

  useEffect(() => {
    if (id) {
      loadProposal();
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const pollStatus = async () => {
      if (statusPollInFlight.current) return;
      statusPollInFlight.current = true;
      try {
        const info = await getProposalStatus(id);
        if (cancelled || !info) return;
        setProposalData((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            status: info.status ?? prev.status,
          };
        });
      } catch (error: any) {
        if (error?.status === 403 || error?.status === 404) {
          if (intervalId) {
            clearInterval(intervalId);
          }
        }
      } finally {
        statusPollInFlight.current = false;
      }
    };

    intervalId = setInterval(pollStatus, 5000);
    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const ownerId = proposalData?.owner_id;
    if (!id || !isAdminRole || !ownerId) {
      setOwnerProfile(null);
      return () => {
        cancelled = true;
      };
    }

    const loadOwnerProfile = async () => {
      try {
        const owner = await getUser(ownerId);
        if (cancelled) return;
        setOwnerProfile({
          id: owner.id,
          full_name: owner.full_name,
          profile_photo: owner.profile_photo ?? null,
        });
      } catch {
        if (cancelled) return;
        setOwnerProfile({
          id: ownerId,
          full_name: undefined,
          profile_photo: null,
        });
      }
    };

    loadOwnerProfile();

    return () => {
      cancelled = true;
    };
  }, [id, isAdminRole, proposalData?.owner_id]);

  const loadProposal = async (options?: { preserveStep?: boolean }) => {
    const { preserveStep = false } = options || {};

    try {
      setLoading(true);

      if (!id) return;

      const proposal = await getProposal(id);
      
      if (!proposal) {
        toast({
          title: "Proposal not found",
          description: "The requested proposal could not be found.",
          variant: "destructive",
        });
        navigate("/proposals");
        return;
      }
      
      const passengers = proposal.passengers || [];
      const tripLegs = proposal.trip_legs || [];
      const airportServices = proposal.airport_services || [];
      const transfers = proposal.transfers || [];
      const flights = proposal.flights || [];
      const accommodations = proposal.accommodations || [];
      const tours = proposal.tours || [];
      const attractions = proposal.attractions || [];
      const additionalRequests = proposal.additional_requests || [];
      const insurance = proposal.insurance || [];
      const handlingFee = proposal.handling_fee || [];

      const lastStep = (proposal as any).last_step ?? 1;

      if (!preserveStep) {
        setCurrentStep(lastStep);

        const stepsToVisit = new Set<number>();
        for (let i = 1; i <= lastStep; i++) {
          stepsToVisit.add(i);
        }
        setVisitedSteps(stepsToVisit);
      } else {
        setVisitedSteps(prev => {
          const updatedSteps = new Set(prev);
          const maxVisitedStep = Math.max(lastStep, currentStep);

          for (let i = 1; i <= maxVisitedStep; i++) {
            updatedSteps.add(i);
          }

          return updatedSteps;
        });
      }

      const nextProposalData = {
        ...proposal,
        currency: normalizeProposalCurrency((proposal as any).currency),
        passengerCount: {
          adults: (proposal as any).pax_adults ?? 0,
          children: (proposal as any).pax_children ?? 0,
          infants: (proposal as any).pax_infants ?? 0,
        },
        detailedPassengerMode: (proposal as any).passenger_mode_detailed ?? false,
        passengers: passengers || [],
        tripLegs: tripLegs || [],
        airportServices: airportServices || [],
        transfers: transfers || [],
        flights: flights || [],
        accommodations: accommodations || [],
        tours: tours || [],
        attractions: attractions || [],
        additionalRequests: additionalRequests || [],
        insurance: insurance || [],
        handlingFee: handlingFee || []
      };
      setProposalData(nextProposalData);
      hasLoadedRef.current = true;
      lastSavedSnapshotRef.current = snapshotProposal(nextProposalData);
      loadedHadItemsRef.current = [
        tours,
        attractions,
        transfers,
        flights,
        accommodations,
        additionalRequests,
        airportServices,
        insurance,
      ].some((list) => Array.isArray(list) && list.length > 0);
    } catch (error: any) {
      toast({
        title: "Error loading proposal",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep < steps.length) {
      const nextStep = currentStep + 1;

      if (nextStep === steps.length && id) {
        await loadProposal({ preserveStep: true });
      }

      setCurrentStep(nextStep);
      setVisitedSteps(prev => new Set(prev).add(nextStep));
    }
  };

  const normalizePhoneNumber = (phone?: string | null) =>
    (phone || "").replace(/\D/g, "");

  const getWhatsAppMessage = (clientName: string, link: string) => {
    const templates: Record<string, (name: string, proposalLink: string) => string> = {
      pt_br: (name, proposalLink) =>
        `Olá ${name}, tudo bem?\nSegue o link com a proposta detalhada da sua viagem:\n${proposalLink}\nQualquer dúvida, estou à disposição.`,
      en: (name, proposalLink) =>
        `Hi ${name}, how are you?\nHere is the link with the detailed proposal for your trip:\n${proposalLink}\nIf you have any questions, just let me know.`,
    };

    const template = templates.en;
    return template(clientName, link);
  };

  const openWhatsAppWithProposal = async (publicLink: string) => {
    if (!proposalData.client_id) {
      toast({
        title: "WhatsApp unavailable",
        description: "Unable to open WhatsApp because the client does not have a valid phone number on record.",
      });
      return;
    }

    try {
      const client = await getClient(proposalData.client_id);

      const rawPhone = client?.mobile || client?.alternate_phone || "";
      const normalizedPhone = normalizePhoneNumber(rawPhone);

      if (!normalizedPhone || normalizedPhone.length < 10) {
        toast({
          title: "WhatsApp unavailable",
          description: "Unable to open WhatsApp because the client does not have a valid phone number on record.",
        });
        return;
      }

      const clientName = `${client?.first_name ?? ""} ${client?.last_name ?? ""}`.trim() || client?.company || "Client";
      const message = getWhatsAppMessage(clientName, publicLink);
      const whatsappUrl = `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(message)}`;

      window.open(whatsappUrl, "_blank");
    } catch (error: any) {
      toast({
        title: "Error opening WhatsApp",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = async (stepId: number) => {
    if (stepId === steps.length && id) {
      await loadProposal({ preserveStep: true });
    }

    setCurrentStep(stepId);
    setVisitedSteps(prev => new Set(prev).add(stepId));
  };

  /**
   * Persists the draft. Returns the outcome instead of only toasting, so silent callers
   * (which deliberately suppress the toast) can still tell a save apart from a failure
   * and react — rolling back an optimistic update, for instance.
   */
  const handleSave = async (options?: { silent?: boolean; skipReload?: boolean; syncCatalog?: boolean; dataOverride?: any }): Promise<SaveOutcome> => {
    const silent = options?.silent ?? false;
    const skipReload = options?.skipReload ?? false;
    const syncCatalog = options?.syncCatalog ?? !silent;
    // Prefer the explicit override (passed when a setState hasn't flushed yet);
    // fall back to the ref so the rest of handleSave always sees fresh data.
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const proposalData = options?.dataOverride ?? proposalDataRef.current;
    if (isReadOnly) {
      if (!silent) {
        toast({
          title: "Proposal locked",
          description: "This proposal is locked and cannot be edited in the current status.",
          variant: "destructive",
        });
      }
      return { ok: false, error: new Error("Proposal is locked") };
    }
    // Root-cause guard against the total-wipe bug: for an existing proposal, never save before
    // loadProposal has populated state. Until then proposalData is the empty initial object, and
    // a save (e.g. Preview clicked while the proposal is still loading) would PUT every list empty
    // and wipe all items. A brand-new proposal (no id) legitimately starts empty, so it is exempt.
    if (id && !hasLoadedRef.current) {
      if (import.meta.env.DEV) {
        console.warn("[Draft Save] Skipped: proposal has not finished loading yet.");
      }
      return { ok: false, error: new Error("Proposal not loaded yet") };
    }
    const saveStartMs = Date.now();
    let saveOutcome: SaveOutcome = { ok: false };
    setIsSaving(true);
    try {
      if (!silent) {
        setLoading(true);
      }
      if (import.meta.env.DEV) {
        console.info("[Draft Save] Started", { silent, skipReload, syncCatalog, step: currentStep });
      }

      const proposalId = id || undefined;

      const sanitizeRelatedItems = (
        items: any[] | undefined,
        proposal_id: string,
        relationKeys: string[] = [],
        isEmptyCheck?: (item: any) => boolean,
        excludeFields: string[] = []
      ) => {
        if (isEmptyCheck) {
          return validateAndSanitizeItems(
            items,
            proposal_id,
            excludeFields,
            relationKeys,
            isEmptyCheck
          );
        }
        return validateAndSanitizeItems(items, proposal_id, excludeFields, relationKeys);
      };
      
      const { 
        passengers, 
        tripLegs, 
        detailedPassengerMode, 
        passengerCount,
        airportServices,
        transfers,
        flights,
        accommodations,
        tours,
        attractions,
        additionalRequests,
        insurance,
        handlingFee,
        termsAndConditions,
        ...mainProposalData 
      } = proposalData;

      const syncStartMs = Date.now();
      const syncedAdditionalRequests = await syncReusableAdditionalRequests(additionalRequests, {
        persistCatalog: syncCatalog,
      });
      if (syncCatalog) {
        setProposalData((prev: any) => ({
          ...prev,
          additionalRequests: syncedAdditionalRequests,
        }));
      }
      if (import.meta.env.DEV) {
        console.info("[Draft Save] Additional requests sync finished", {
          durationMs: Date.now() - syncStartMs,
          itemCount: syncedAdditionalRequests.length,
          syncCatalog,
        });
      }

      const normalizedCurrentStatus = (proposalData?.status || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/-/g, "_");

      const preserveStatuses = new Set([
        "sent",
        "approved_to_be_paid",
        "needs_fix_sales",
        "in_review_to_be_canceled",
        "paid_internal_review",
        "ready_to_book",
        "canceled",
        "booked",
        "awaiting_arrival",
        "in_house",
        "request_feedback",
        "done",
      ]);

      const draftStatus = preserveStatuses.has(normalizedCurrentStatus)
        ? normalizedCurrentStatus
        : "in_progress";

      const normalizedProposalData = {
        ...mainProposalData,
        last_step: currentStep,
        currency: normalizeProposalCurrency(mainProposalData.currency),
        status: draftStatus,
        ...(passengerCount
          ? {
              pax_adults: passengerCount.adults ?? 0,
              pax_children: passengerCount.children ?? 0,
              pax_infants: passengerCount.infants ?? 0,
            }
          : {}),
        ...(typeof detailedPassengerMode !== 'undefined'
          ? { passenger_mode_detailed: !!detailedPassengerMode }
          : {}),
        ...(typeof termsAndConditions !== 'undefined' && !('terms_and_conditions' in mainProposalData)
          ? { terms_and_conditions: termsAndConditions }
          : {}),
      };
      
      const handlingFeeArray = Array.isArray(handlingFee)
        ? handlingFee
        : (handlingFee ? [handlingFee] : []);

      if (id) {
      const validPassengers = passengers?.length ? sanitizeRelatedItems(passengers, proposalId as string) : [];
      const validTripLegs = tripLegs?.length ? sanitizeRelatedItems(tripLegs, proposalId as string) : [];
      const validAirportServices = airportServices?.length ? sanitizeRelatedItems(airportServices, proposalId, ["airport_services"], (service) => {
        return !hasAnyData(service, ["id", "created_at", "updated_at", "proposal_id", "airport_services"]);
      }) : [];
        const validTransfers = transfers?.length ? sanitizeRelatedItems(transfers, proposalId, ["transfers"], (transfer) => {
          return !hasAnyData(transfer, ["id", "created_at", "updated_at", "proposal_id", "transfers"]);
        }) : [];
        const validFlights = flights?.length ? sanitizeRelatedItems(flights, proposalId, [], (flight) => {
          return !hasAnyData(flight, ["id", "created_at", "updated_at", "proposal_id"]);
        }) : [];
        const validAccommodations = accommodations?.length ? sanitizeRelatedItems(accommodations, proposalId, ["hotels", "hotel_rooms"], (acc) => {
          return !hasAnyData(acc, ["id", "created_at", "updated_at", "proposal_id", "hotels", "hotel_rooms"]);
        }) : [];
        const validTours = tours?.length ? sanitizeRelatedItems(tours, proposalId, ["tours"], (tour) => {
          return !hasAnyData(tour, ["id", "created_at", "updated_at", "proposal_id", "tours"]);
        }) : [];
        const validAttractions = attractions?.length ? sanitizeRelatedItems(attractions, proposalId, ["attractions", "attraction_tickets"], (attr) => {
          const attrWithoutDefaultQuantity = { ...attr };
          if (attrWithoutDefaultQuantity.ticket_quantity === 1) {
            delete attrWithoutDefaultQuantity.ticket_quantity;
          }
          return !hasAnyData(attrWithoutDefaultQuantity, ["id", "created_at", "updated_at", "proposal_id", "attractions", "attraction_tickets"]);
        }) : [];
        const validRequests = syncedAdditionalRequests?.length ? sanitizeRelatedItems(syncedAdditionalRequests, proposalId, [], (req) => {
          return !hasAnyData(req, ["id", "created_at", "updated_at", "proposal_id", "save_to_catalog"]);
        }, ["save_to_catalog"]) : [];
        const validInsurance = insurance?.length ? sanitizeRelatedItems(insurance, proposalId, [], (ins) => {
          return !hasAnyData(ins, ["id", "created_at", "updated_at", "proposal_id"]);
        }) : [];
        const validHandlingFees = handlingFeeArray?.length ? sanitizeRelatedItems(handlingFeeArray, proposalId, [], (fee) => {
          return !hasAnyData(fee, ["id", "created_at", "updated_at", "proposal_id"]);
        }) : [];

      // Client-side safety net mirroring the backend guard: never send a PUT that would clear
      // every service item of a proposal that was loaded with items. That only happens when the
      // local state is stale/empty (the data-loss bug), never from a real edit.
      const wouldClearAllItems =
        !validTours.length &&
        !validAttractions.length &&
        !validTransfers.length &&
        !validAirportServices.length &&
        !validFlights.length &&
        !validAccommodations.length &&
        !validRequests.length &&
        !validInsurance.length;
      if (wouldClearAllItems && loadedHadItemsRef.current) {
        if (import.meta.env.DEV) {
          console.warn("[Draft Save] Aborted: refusing to clear all items of a non-empty proposal (stale state).");
        }
        return { ok: false, error: new Error("Refusing to clear all proposal items") };
      }

      const updateStartMs = Date.now();
      await updateProposal(id, {
        ...normalizedProposalData,
        client_id: normalizedProposalData.client_id ?? proposalData.client_id,
        owner_id: normalizedProposalData.owner_id ?? proposalData.owner_id,
        internal_reference: normalizedProposalData.internal_reference ?? proposalData.internal_reference,
        main_destination: normalizedProposalData.main_destination ?? proposalData.main_destination,
        context_observations: normalizedProposalData.context_observations ?? proposalData.context_observations,
        language: normalizedProposalData.language ?? proposalData.language,
        currency: normalizedProposalData.currency ?? proposalData.currency ?? "usd",
        priority: normalizedProposalData.priority ?? proposalData.priority,
        status: normalizedProposalData.status ?? proposalData.status,
        last_step: normalizedProposalData.last_step ?? currentStep,
        pax_adults: normalizedProposalData.pax_adults ?? proposalData.pax_adults ?? 0,
        pax_children: normalizedProposalData.pax_children ?? proposalData.pax_children ?? 0,
        pax_infants: normalizedProposalData.pax_infants ?? proposalData.pax_infants ?? 0,
        passenger_mode_detailed: normalizedProposalData.passenger_mode_detailed ?? proposalData.passenger_mode_detailed,
        terms_and_conditions: normalizedProposalData.terms_and_conditions ?? proposalData.terms_and_conditions,
        passengers: validPassengers,
        trip_legs: validTripLegs,
        airport_services: validAirportServices,
        transfers: validTransfers,
        flights: validFlights,
        accommodations: validAccommodations,
        tours: validTours,
        attractions: validAttractions,
        additional_requests: validRequests,
        insurance: validInsurance,
        handling_fee: validHandlingFees,
      });
      lastSavedSnapshotRef.current = snapshotProposal(proposalDataRef.current);
      if (import.meta.env.DEV) {
        console.info("[Draft Save] Proposal update finished", {
          durationMs: Date.now() - updateStartMs,
          passengers: validPassengers.length,
          tripLegs: validTripLegs.length,
          airportServices: validAirportServices.length,
          transfers: validTransfers.length,
          flights: validFlights.length,
          accommodations: validAccommodations.length,
          tours: validTours.length,
          attractions: validAttractions.length,
          additionalRequests: validRequests.length,
          insurance: validInsurance.length,
          handlingFee: validHandlingFees.length,
        });
      }

        if (!silent) {
          toast({
            title: "Success",
            description: "Proposal saved successfully",
          });
        }

        if (!skipReload) {
          const reloadStartMs = Date.now();
          await loadProposal({ preserveStep: true });
          if (import.meta.env.DEV) {
            console.info("[Draft Save] Reload finished", {
              durationMs: Date.now() - reloadStartMs,
            });
          }
        }
      } else {
        const insertData = {
          title: normalizedProposalData.title,
          client_id: normalizedProposalData.client_id,
          main_destination: normalizedProposalData.main_destination,
          // Required by the API on create (validateLeadFields). Both were missing from this
          // payload, so every "Save Draft" on a brand-new proposal came back 400 no matter what
          // the user picked in the form -- the update path below always sent them correctly.
          proposal_category: normalizedProposalData.proposal_category,
          sector: normalizedProposalData.sector,
          vat_exempt: !!normalizedProposalData.vat_exempt,
          context_observations: normalizedProposalData.context_observations,
          pax_adults: normalizedProposalData.pax_adults || 0,
          pax_children: normalizedProposalData.pax_children || 0,
          pax_infants: normalizedProposalData.pax_infants || 0,
          language: normalizedProposalData.language || 'en',
          currency: normalizeProposalCurrency(normalizedProposalData.currency),
          priority: normalizedProposalData.priority || 'medium',
          internal_reference: normalizedProposalData.internal_reference,
          pricing_adjustment_type: normalizedProposalData.pricing_adjustment_type || "none",
          pricing_adjustment_percentage: normalizedProposalData.pricing_adjustment_percentage || 0,
          owner_id: normalizedProposalData.owner_id,
          status: normalizedProposalData.status || 'draft',
          last_step: currentStep,
        };
        
        const newProposal = await createProposal(insertData as any);

        if (!newProposal) throw new Error("Proposal not created");

        
        toast({
          title: "Success",
          description: "Proposal created successfully",
        });
        navigate("/proposals");
      }
      saveOutcome = { ok: true };
    } catch (error: any) {
      saveOutcome = { ok: false, error };
      if (!silent) {
        toast({
          title: "Error saving proposal",
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      if (import.meta.env.DEV) {
        console.info("[Draft Save] Completed", {
          totalDurationMs: Date.now() - saveStartMs,
          silent,
          skipReload,
          syncCatalog,
        });
      }
      if (!silent) {
        setLoading(false);
      }
      setIsSaving(false);
    }
    return saveOutcome;
  };

  const buildPublicProposalUrl = (token: string, options?: { pdf?: boolean }) => {
    const url = `${window.location.origin}/proposal/share/${token}`;
    return options?.pdf ? `${url}?download_pdf=1` : url;
  };

  const createShareableProposalLink = async (pricingView: ProposalPricingView = generationPricingView) => {
    if (!id) throw new Error("Proposal ID is required");
    const link = await getOrCreateProposalPublicLink(id, pricingView);
    return buildPublicProposalUrl(link.token);
  };

  const handleGenerateProposalLink = async () => {
    if (!id) {
      toast({
        title: "Save required",
        description: "Save the proposal before generating a public link.",
        variant: "destructive",
      });
      return;
    }
    try {
      setGeneratingLink(true);
      if (!isReadOnly) {
        await handleSave({ skipReload: true });
      }
      const link = await createShareableProposalLink(generationPricingView);
      setShareableLink(link);
      setShowShareDialog(true);
    } catch (error: any) {
      toast({
        title: "Error generating link",
        description: error?.message || "Unable to generate the public proposal link.",
        variant: "destructive",
      });
    } finally {
      setGeneratingLink(false);
    }
  };

  // PDF generation is being reworked; the buttons that call this are disabled
  // in SummaryStep. Stub kept so the callback wiring stays intact.
  const handleGeneratePdf = async (_pricingView: ProposalPricingView = generationPricingView) => {
    toast({
      title: "PDF generation is being reworked",
      description: "This action is temporarily disabled.",
    });
  };

  const handleFinish = async () => {
    if (isReadOnly) {
      toast({
        title: "Proposal locked",
        description: "This proposal is locked and cannot be edited in the current status.",
        variant: "destructive",
      });
      return;
    }
    if (totalPassengers <= 0) {
      toast({
        title: "Passengers required",
        description: "Please add at least one passenger (adult, child, or infant) to finish the proposal.",
        variant: "destructive",
      });
      return;
    }
    try {
      setLoading(true);
      setFinishing(true);
      
      const proposalId = id as string | undefined;
      if (!proposalId) throw new Error("Proposal ID is required");

      await handleSave();

      const sanitizeForInsert = (
        items: any[] | undefined, 
        relationKeys: string[] = [],
        isEmptyCheck?: (item: any) => boolean,
        excludeFields: string[] = []
      ) => {
        return validateAndSanitizeItems(
          items,
          proposalId,
          excludeFields,
          relationKeys,
          isEmptyCheck
        );
      };

      const {
        passengers,
        tripLegs,
        airportServices,
        transfers,
        flights,
        accommodations,
        tours,
        attractions,
        additionalRequests,
        insurance,
        handlingFee,
        passengerCount,
        detailedPassengerMode,
        termsAndConditions,
        ...mainProposalData
      } = proposalData;

      const syncedAdditionalRequests = await syncReusableAdditionalRequests(additionalRequests);
      setProposalData((prev: any) => ({
        ...prev,
        additionalRequests: syncedAdditionalRequests,
      }));

      const normalizedProposalData = {
        ...mainProposalData,
        last_step: currentStep,
        currency: normalizeProposalCurrency(mainProposalData.currency),
        ...(passengerCount
          ? {
              pax_adults: passengerCount.adults ?? 0,
              pax_children: passengerCount.children ?? 0,
              pax_infants: passengerCount.infants ?? 0,
            }
          : {}),
        ...(typeof detailedPassengerMode !== 'undefined'
          ? { passenger_mode_detailed: !!detailedPassengerMode }
          : {}),
        ...(typeof termsAndConditions !== 'undefined' && !('terms_and_conditions' in mainProposalData)
          ? { terms_and_conditions: termsAndConditions }
          : {}),
      };

      const handlingFeeArray = Array.isArray(handlingFee) ? handlingFee : handlingFee ? [handlingFee] : [];
      
      const validPassengers = passengers?.length ? sanitizeForInsert(passengers) : [];
      const validTripLegs = tripLegs?.length ? sanitizeForInsert(tripLegs) : [];
      const validAirportServices = airportServices?.length ? sanitizeForInsert(airportServices, ["airport_services"], (service) => {
        return !hasAnyData(service, ["id", "created_at", "updated_at", "proposal_id", "airport_services"]);
      }) : [];
      const validTransfers = transfers?.length ? sanitizeForInsert(transfers, ["transfers"], (transfer) => {
        return !hasAnyData(transfer, ["id", "created_at", "updated_at", "proposal_id", "transfers"]);
      }) : [];
      const validFlights = flights?.length ? sanitizeForInsert(flights, [], (flight) => {
        return !hasAnyData(flight, ["id", "created_at", "updated_at", "proposal_id"]);
      }) : [];
      const validAccommodations = accommodations?.length ? sanitizeForInsert(accommodations, ["hotels", "hotel_rooms"], (acc) => {
        return !hasAnyData(acc, ["id", "created_at", "updated_at", "proposal_id", "hotels", "hotel_rooms"]);
      }) : [];
      const validTours = tours?.length ? sanitizeForInsert(tours, ["tours"], (tour) => {
        return !hasAnyData(tour, ["id", "created_at", "updated_at", "proposal_id", "tours"]);
      }) : [];
      const validAttractions = attractions?.length ? sanitizeForInsert(attractions, ["attractions", "attraction_tickets"], (attr) => {
        const attrWithoutDefaultQuantity = { ...attr };
        if (attrWithoutDefaultQuantity.ticket_quantity === 1) {
          delete attrWithoutDefaultQuantity.ticket_quantity;
        }
        return !hasAnyData(attrWithoutDefaultQuantity, ["id", "created_at", "updated_at", "proposal_id", "attractions", "attraction_tickets"]);
      }) : [];
      const validRequests = syncedAdditionalRequests?.length ? sanitizeForInsert(syncedAdditionalRequests, [], (req) => {
        return !hasAnyData(req, ["id", "created_at", "updated_at", "proposal_id", "save_to_catalog"]);
      }, ["save_to_catalog"]) : [];
      const validInsurance = insurance?.length ? sanitizeForInsert(insurance, [], (ins) => {
        return !hasAnyData(ins, ["id", "created_at", "updated_at", "proposal_id"]);
      }) : [];
      const validHandlingFees = handlingFeeArray?.length ? sanitizeForInsert(handlingFeeArray, [], (fee) => {
        return !hasAnyData(fee, ["id", "created_at", "updated_at", "proposal_id"]);
      }) : [];

      await updateProposal(proposalId, {
        ...normalizedProposalData,
        passengers: validPassengers,
        trip_legs: validTripLegs,
        airport_services: validAirportServices,
        transfers: validTransfers,
        flights: validFlights,
        accommodations: validAccommodations,
        tours: validTours,
        attractions: validAttractions,
        additional_requests: validRequests,
        insurance: validInsurance,
        handling_fee: validHandlingFees,
      });

      await updateProposalStatus(id, {
        status: "sent",
      });

      const link = await createShareableProposalLink(generationPricingView);
      setShareableLink(link);

      toast({
        title: "Proposal completed!",
        description: "The proposal has been sent successfully.",
      });

      void openWhatsAppWithProposal(link);
      setShowShareDialog(true);

    } catch (error: any) {
      toast({
        title: "Error completing proposal",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setFinishing(false);
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      toast({
        title: "Link copied!",
        description: "The link has been copied to the clipboard.",
      });
    } catch (error) {
      toast({
        title: "Error copying link",
        description: "Unable to copy the link.",
        variant: "destructive",
      });
    }
  };

  const openProposal = () => {
    window.open(shareableLink, '_blank');
  };

  const loadManagers = async () => {
    try {
      setLoadingManagers(true);
      const users = await lookupUsersByRoles(["general_manager", "ceo"]);
      setManagers((users || []).map((manager) => ({
        id: manager.id,
        full_name: manager.full_name,
        email: manager.email,
      })));
    } catch (error: any) {
      toast({
        title: "Error loading managers",
        description: error?.message || "Could not load manager list.",
        variant: "destructive",
      });
    } finally {
      setLoadingManagers(false);
    }
  };

  const handleOpenCancelRequestModal = () => {
    setShowCancelRequestModal(true);
    void loadManagers();
  };

  const handleRequestCancellation = async () => {
    if (!id || requestingCancellation) return;
    if (!cancelReasonInput.trim() || !selectedManagerId) {
      toast({
        title: "Required fields",
        description: "Provide a justification and select a manager reviewer.",
        variant: "destructive",
      });
      return;
    }

    try {
      setRequestingCancellation(true);
      await requestProposalCancellation(id, {
        reason: cancelReasonInput.trim(),
        manager_id: selectedManagerId,
      });
      toast({
        title: "Request submitted",
        description: "The proposal has been sent for cancellation review.",
      });
      setShowCancelRequestModal(false);
      setCancelReasonInput("");
      setSelectedManagerId("");
      await loadProposal({ preserveStep: true });
    } catch (error: any) {
      toast({
        title: "Error requesting cancellation",
        description: error?.message || "Could not request cancellation.",
        variant: "destructive",
      });
    } finally {
      setRequestingCancellation(false);
    }
  };

  const previewCurrentProposal = async (pricingView: ProposalPricingView = generationPricingView) => {
    if (!id) {
      toast({
        title: "Preview unavailable",
        description: "Save the proposal first to generate a preview link.",
        variant: "destructive",
      });
      return;
    }

    try {
      setPreviewingProposal(true);
      if (!isReadOnly) {
        const currentSnapshot = snapshotProposal(proposalDataRef.current);
        // Only save when there is a real, detectable change. A null lastSavedSnapshot means the
        // proposal hasn't finished loading yet — treating that as "dirty" would fire a save against
        // the empty initial state and wipe every item, so we explicitly do NOT save in that case.
        const isDirty =
          currentSnapshot !== null &&
          lastSavedSnapshotRef.current !== null &&
          currentSnapshot !== lastSavedSnapshotRef.current;
        if (isDirty) {
          await handleSave({ skipReload: true, silent: true });
        }
      }
      const link = await createShareableProposalLink(pricingView);
      window.open(link, "_blank");
    } catch (error: any) {
      toast({
        title: "Preview unavailable",
        description: error?.message || "Unable to generate a preview link.",
        variant: "destructive",
      });
    } finally {
      setPreviewingProposal(false);
    }
  };


  const CurrentStepComponent = steps[currentStep - 1].component;
  const progressPercentage = (currentStep / steps.length) * 100;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink onClick={() => navigate("/proposals")} className="cursor-pointer">
                  Proposals
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {id ? "Edit Proposal" : "New Proposal"}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
        </Breadcrumb>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/proposals")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>

        {id && (
          <ProposalBuilderHeader
            proposalId={id}
            proposalTitle={proposalData?.title}
            proposalStatus={proposalData?.status}
            internalReference={proposalData?.internal_reference}
            ownerId={proposalData?.owner_id}
            ownerName={ownerProfile?.full_name}
            ownerPhoto={ownerProfile?.profile_photo}
            showOwnerInfo={isAdminRole}
            canUnlockProposal={!isFinanceStaff}
            editUnlocked={proposalData?.edit_unlocked}
            currentUserId={user?.id ?? null}
            currentUserRole={userRole}
            cancellationReason={proposalData?.cancellation_reason}
            onStatusChange={() => loadProposal({ preserveStep: true })}
            onInvoiceUploaded={() => navigate("/proposals")}
          />
        )}

        {id && isInProgress && (
          <div>
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 shadow-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <p className="text-sm font-medium text-amber-900">
                This proposal is still In Progress. To send it to the client, finish the proposal first.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{currentStep} of {steps.length}</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        <div className="flex gap-6">
          <div className="w-72 shrink-0">
            <div className="sticky top-4 space-y-1 rounded-lg border bg-card p-4">
              <h3 className="font-semibold mb-4 px-2">Steps</h3>
              {steps.map((step) => {
                const StepIcon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = !!stepCompletion[step.id];
                const isVisited = visitedSteps.has(step.id) || isCompleted;
                
                return (
                  <button
                    key={step.id}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : isVisited
                        ? "hover:bg-muted/50 text-foreground"
                        : "text-muted-foreground hover:bg-muted/30"
                    }`}
                    onClick={() => handleStepClick(step.id)}
                  >
                    <div className="shrink-0">
                      {isCompleted ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : isActive ? (
                        <div className="h-5 w-5 rounded-full bg-primary-foreground flex items-center justify-center">
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        </div>
                      ) : (
                        <Circle className="h-5 w-5" />
                      )}
                    </div>
                    <StepIcon className="h-4 w-4 shrink-0" />
                    <span className={`text-sm flex-1 text-left ${isActive ? "font-medium" : ""}`}>
                      {step.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1">
            <div className="rounded-lg border bg-card shadow-sm">
              <div className="border-b bg-muted/30 p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const CurrentIcon = steps[currentStep - 1].icon;
                      return <CurrentIcon className="h-6 w-6 text-primary" />;
                    })()}
                    <div>
                      <h1 className="text-2xl font-bold">{steps[currentStep - 1].name}</h1>
                      <p className="text-sm text-muted-foreground mt-1">
                        Step {currentStep} of {steps.length}
                      </p>
                    </div>
                    {isSummaryStep && canRequestCancellation && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleOpenCancelRequestModal}
                        className="h-10 rounded-xl border-rose-300 bg-rose-50 px-4 font-semibold text-rose-800 shadow-sm transition-all hover:border-rose-400 hover:bg-rose-100 hover:text-rose-900"
                      >
                        <Ban className="mr-2 h-4 w-4" />
                        Request Cancellation Review
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {id && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { void previewCurrentProposal(); }}
                        disabled={previewingProposal}
                      >
                        {previewingProposal ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <ExternalLink className="mr-2 h-4 w-4" />
                        )}
                        Preview Proposal
                      </Button>
                    )}
                    {!isReadOnly && !isFollowUpStep && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { void handleSave({ skipReload: true }); }}
                        disabled={loading}
                        className="border-dashed border-emerald-300 bg-emerald-50/70 text-emerald-800 hover:bg-emerald-100"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="mr-2 h-4 w-4" />
                            Save Draft
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <CurrentStepComponent
                  data={proposalData}
                  onChange={handleProposalDataChange}
                  userRole={userRole}
                  readOnly={isReadOnly}
                  onPreview={previewCurrentProposal}
                  onGeneratePdf={handleGeneratePdf}
                  onFinish={handleFinish}
                  onApplyAdjustment={handleApplyAdjustment}
                  isNeedsFixSales={isNeedsFixSales}
                  loading={loading}
                  previewingProposal={previewingProposal}
                  generatingPdf={generatingPdf}
                />
              </div>
              
              <div className="border-t p-4 flex items-center justify-between bg-muted/30">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handlePrevious}
                  disabled={currentStep === 1 || loading}
                  className="h-10 px-4 text-sm"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Previous
                </Button>
                
                <div className="flex gap-4 items-center">
                  {currentStep < steps.length && !isFollowUpStep && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleSave({ skipReload: true })}
                      disabled={loading || isReadOnly}
                      className="border-dashed border-emerald-300 bg-emerald-50/70 text-emerald-800 hover:bg-emerald-100"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Save Draft
                    </Button>
                  )}

                  {currentStep < steps.length && (
                    <Button
                      type="button"
                      onClick={handleNext}
                      disabled={loading}
                      className="h-10 px-5 text-sm font-semibold"
                    >
                      Next
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={finishing}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Finalizing Proposal</DialogTitle>
            <DialogDescription>
              Please wait while we validate and process your proposal data.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Validating proposal data...</span>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isSaving || previewingProposal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {previewingProposal ? "Preparing preview" : "Saving proposal"}
            </DialogTitle>
            <DialogDescription>
              {previewingProposal
                ? "Saving your changes and generating the preview link..."
                : "Please wait while your changes are saved."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">
              {previewingProposal ? "This usually takes a few seconds." : "Sending changes to the server..."}
            </span>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Proposal Completed!</DialogTitle>
            <DialogDescription>
              Shareable link generated successfully. Copy and send it to the client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="grid flex-1 gap-2">
                <input
                  readOnly
                  value={shareableLink}
                  className="w-full px-3 py-2 text-sm bg-muted rounded-md"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={copyLink} className="flex-1">
                <Copy className="mr-2 h-4 w-4" />
                Copy Link
              </Button>
              <Button onClick={openProposal} variant="outline" className="flex-1">
                <ExternalLink className="mr-2 h-4 w-4" />
                View
              </Button>
            </div>
            <Button 
              onClick={() => {
                setShowShareDialog(false);
                navigate("/proposals");
              }} 
              variant="secondary" 
              className="w-full"
            >
              Back to Proposals
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelRequestModal} onOpenChange={setShowCancelRequestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request proposal cancellation</DialogTitle>
            <DialogDescription>
              Provide a reason and select the manager that will review this cancellation request.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="cancellation-reason">
                Justification <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="cancellation-reason"
                rows={4}
                value={cancelReasonInput}
                onChange={(e) => setCancelReasonInput(e.target.value)}
                placeholder="Explain why this proposal should be canceled..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="manager-select">
                Manager reviewer <span className="text-destructive">*</span>
              </Label>
              <Select value={selectedManagerId} onValueChange={setSelectedManagerId} disabled={loadingManagers}>
                <SelectTrigger id="manager-select">
                  <SelectValue placeholder={loadingManagers ? "Loading..." : "Select manager"} />
                </SelectTrigger>
                <SelectContent>
                  {managers.map((manager) => (
                    <SelectItem key={manager.id} value={manager.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{manager.full_name}</span>
                        <span className="text-xs text-muted-foreground">{manager.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelRequestModal(false)} disabled={requestingCancellation}>
              Close
            </Button>
            <Button
              variant="destructive"
              onClick={handleRequestCancellation}
              disabled={requestingCancellation || !cancelReasonInput.trim() || !selectedManagerId}
            >
              {requestingCancellation ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Request"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ProposalBuilder;
